/**
 * Aurora — USA marketing landing (English).
 * Separate from the Spanish `Landing.jsx`; do not merge behaviors.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { startSubscriptionCheckout } from "../lib/publicCheckout";
import {
  ClipboardList,
  FileText,
  Stethoscope,
  HeartPulse,
  Check,
  ArrowRight,
  Clock,
  Shield,
  Sparkles,
  ArrowUpRight
} from "lucide-react";

const APP_US_LOGIN_URL = "https://aurorageriatric.com/login";
const APP_US_REGISTER_URL = "https://aurorageriatric.com/register";

/** Spanish / LATAM marketing site (explicit host so footer never loops on U.S. domain when "/" is USA). */
const LATAM_MARKETING_URL = "https://aurorageriatrica.com/";

const solutionItems = [
  {
    icon: ClipboardList,
    title: "Structured intake",
    description: "Capture the right geriatric context up front so each visit starts with clinical focus."
  },
  {
    icon: Stethoscope,
    title: "Integrated assessments",
    description: "Run cognitive, function, and risk-focused assessments in one longitudinal chart."
  },
  {
    icon: FileText,
    title: "Clinical documentation",
    description: "Generate concise, professional visit documentation without repetitive typing."
  },
  {
    icon: HeartPulse,
    title: "Longitudinal care planning",
    description: "Build plans that carry across visits instead of fragmented one-off notes."
  }
];

const benefitItems = [
  "Reduce documentation burden and close visits faster",
  "Keep cognitive, functional, and safety findings in one workflow",
  "Create cleaner notes for follow-up, family communication, and continuity",
  "Purpose-built for geriatric practice in U.S. outpatient settings"
];

const fourMsItems = [
  {
    title: "What Matters",
    description: "Document patient goals and priorities to guide shared decisions over time."
  },
  {
    title: "Medication",
    description: "Track medication context alongside function and cognition to support safer prescribing."
  },
  {
    title: "Mentation",
    description: "Capture cognitive and mood-relevant findings in a structured, longitudinal format."
  },
  {
    title: "Mobility",
    description: "Record gait, function, and fall-related observations to support safer care plans."
  }
];

const useCaseItems = [
  "Annual Wellness Visits (AWV) documentation support",
  "Cognitive-focused follow-up visits",
  "Fall risk and functional status reassessments",
  "Longitudinal geriatric care planning in private practice and group clinics"
];

export default function LandingUS() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafbfc] text-slate-800 antialiased" data-landing="aurora-us">
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors ${
          scrolled ? "border-slate-200/90 bg-white/95 shadow-sm backdrop-blur-md" : "border-transparent bg-white/85 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex flex-col items-start gap-1 text-slate-900 sm:flex-row sm:items-center sm:gap-3">
            <img
              src="/assets/logo/logo-wordmark.png"
              alt="Aurora Geriatric"
              className="h-8 w-auto max-w-[200px] object-contain object-left sm:h-9"
            />
            <span className="rounded-full border border-sky-200/90 bg-sky-50/90 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-900/80">
              U.S. Edition
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" className="text-slate-600" asChild>
              <a href={APP_US_LOGIN_URL}>Sign in</a>
            </Button>
            <Button size="sm" className="rounded-lg bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700" asChild>
              <a href={APP_US_REGISTER_URL}>Start free trial</a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/80 px-4 pb-24 pt-28 sm:px-6 sm:pb-28 sm:pt-32 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-700/90">U.S. Geriatric Workflow Platform</p>
            <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl sm:leading-[1.08]">
              Aurora Geriatric
            </h1>
            <p className="mt-3 text-lg font-medium text-slate-600 sm:text-xl">
              Streamline geriatric visits from intake to documentation.
            </p>
            <p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl sm:leading-relaxed">
              Support AWV, cognitive, and fall-risk focused visits with structured, longitudinal documentation.
            </p>
            <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <Button size="lg" className="h-12 rounded-xl bg-sky-600 px-8 text-base font-semibold text-white shadow-md shadow-sky-600/20 hover:bg-sky-700" asChild>
                <a href={APP_US_REGISTER_URL}>Start free trial</a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 rounded-xl border-slate-200 bg-white px-8 text-base font-semibold text-slate-800 hover:bg-slate-50"
                onClick={startSubscriptionCheckout}
              >
                Subscribe now
              </Button>
            </div>
            <p className="mt-6 text-sm text-slate-500">No credit card required · Full access during trial</p>
          </div>
        </section>

        {/* Problem */}
        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">The problem</h2>
            <p className="mx-auto mt-5 text-center text-base leading-relaxed text-slate-600 sm:text-lg">
              U.S. geriatric visits often require juggling cognitive findings, function, fall risk, medications, and care goals.
              Generic workflows fragment this data across screens, increasing documentation time and reducing clinical clarity.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section className="border-y border-slate-100 bg-white px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">The solution</h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-slate-600">
              One geriatric-first workflow from intake to plan, designed for day-to-day U.S. outpatient care.
            </p>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:gap-8">
              {solutionItems.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="rounded-2xl border border-slate-200/80 bg-slate-50/40 p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                    <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4Ms */}
        <section className="border-t border-slate-100 bg-slate-50/60 px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Aligned with the 4Ms Framework
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-slate-600">
              Keep core geriatric priorities visible and structured at every visit.
            </p>
            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              {fourMsItems.map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Common U.S. Use Cases
            </h2>
            <ul className="mt-10 space-y-4">
              {useCaseItems.map((line) => (
                <li key={line} className="flex gap-3 rounded-xl border border-slate-100 bg-white px-4 py-4 shadow-sm">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                  </span>
                  <span className="text-sm font-medium leading-snug text-slate-800 sm:text-base">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Benefits */}
        <section className="border-t border-slate-100 px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Benefits</h2>
            <ul className="mt-10 space-y-4">
              {benefitItems.map((line) => (
                <li key={line} className="flex gap-3 rounded-xl border border-slate-100 bg-white px-4 py-4 shadow-sm">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                  </span>
                  <span className="text-sm font-medium leading-snug text-slate-800 sm:text-base">{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
              <span className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4 text-sky-600" aria-hidden />
                Faster visits
              </span>
              <span className="inline-flex items-center gap-2">
                <Shield className="h-4 w-4 text-sky-600" aria-hidden />
                Clinical-grade structure
              </span>
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-sky-600" aria-hidden />
                Clear, shareable output
              </span>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="border-t border-slate-100 bg-slate-50/60 px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-lg text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Simple pricing</h2>
            <p className="mt-3 text-slate-600">One professional plan for geriatric clinicians. Cancel anytime.</p>
            <div className="mt-10 rounded-3xl border border-sky-200/80 bg-white p-8 shadow-lg shadow-slate-900/5 ring-1 ring-sky-100/50">
              <p className="text-sm font-medium uppercase tracking-wider text-sky-800/80">Professional</p>
              <p className="mt-4 flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold tracking-tight text-slate-900">$69</span>
                <span className="text-lg text-slate-500">/ month</span>
              </p>
              <p className="mt-2 text-sm text-slate-500">Per clinician · Unlimited patients · Full clinical workflow</p>
              <div className="mt-8 flex flex-col gap-3">
                <Button className="h-11 w-full rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700" asChild>
                  <a href={APP_US_REGISTER_URL}>Start free trial</a>
                </Button>
                <Button
                  variant="outline"
                  className="h-11 w-full rounded-xl border-slate-300 font-semibold text-slate-800 hover:bg-slate-50"
                  onClick={startSubscriptionCheckout}
                >
                  Subscribe now
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl overflow-hidden rounded-3xl border border-sky-200/60 bg-gradient-to-br from-sky-600 via-sky-700 to-slate-900 px-6 py-14 text-center shadow-xl sm:px-12">
            <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              Ready to modernize your geriatric workflow?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-sky-100 sm:text-base">
              Start your trial and see how Aurora Geriatric supports AWV, cognitive, and fall-risk visits with less documentation friction.
            </p>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:justify-center">
              <Button
                size="lg"
                className="h-12 rounded-xl bg-white px-8 text-base font-semibold text-sky-800 shadow-lg hover:bg-sky-50"
                asChild
              >
                <a href={APP_US_REGISTER_URL}>
                  Start free trial
                  <ArrowRight className="ml-2 inline h-4 w-4" aria-hidden />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 rounded-xl border-white/40 bg-white/10 px-8 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/15"
                onClick={startSubscriptionCheckout}
              >
                Subscribe now
                <ArrowUpRight className="ml-2 inline h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 text-center text-sm text-slate-500 sm:flex-row sm:text-left">
          <p>© {new Date().getFullYear()} Aurora Geriatric. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-6">
            <a href={LATAM_MARKETING_URL} className="hover:text-slate-800">
              Spanish site
            </a>
            <a href={APP_US_LOGIN_URL} className="hover:text-slate-800">Sign in</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
